// The call window's CRM pane, the left half (docs/coaching/COLD-OUTBOUND-BASELINE.md section 4b): title and provenance,
// then the nine fixed slots in 4b's order (Who, Where we stand, Last time, Open loop, Why them, Competitor, Do not say,
// Deal, Links), the activity log, the disposition buttons and Open in CRM. Every row is a fixed reservation: a slot with
// nothing shows a quiet dash, before the card arrives a quiet placeholder bar, and no slot ever collapses or grows. The
// old Details block (Company, Stage) is gone: the title and who rows carry them. Lane 5 owns this file
// (docs/design/CALL-POP-KRISP-BAR.md section 4); crmSlots.ts derives the slots. CallWindowStyles concatenates CRM_CSS after
// the window's own CSS. Icons through ui/Icon.tsx only.
//
// data-slot names: the window's fixed slot list (CALL_WINDOW_SLOTS in CallWindowView.tsx) measures crm-who (Who),
// crm-body (slots 2 to 9 and the log), crm-next (Open loop: the old next step), crm-details (Deal, which took over the old
// Details block's stage and owner) and crm-history (the activity log). Every one of the nine slots and the log also
// carries data-crm-slot with its 4b key, so tests can measure each of them.
import type { CallContextCard } from '@bcio-voice/contracts';
import { Fragment } from 'react';
import { ExternalLink } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import type { CrmPaneModel } from './callWindowModel.ts';
import { type CrmSlot, type CrmSlotKey, crmSlotsModel, slotEmpty, slotText } from './crmSlots.ts';

/** The disposition shown as chosen, the one being saved, and the slot's status line. */
export interface CrmDispoModel { selected: string | null; busy: string | null; status: string; warn: boolean }

export const CRM_CSS = `
/* overflow: clip on every box that does not scroll by design: tabbing to the Deck or Links link never scrolls the pane,
   so no slot moves under the Who block. A clipped box is not a scroll container, so each also sets min-width: 0 (and
   min-height: 0) to keep the zero automatic minimum that overflow: hidden gave it, and every grid names its one
   column minmax(0, 1fr): a nowrap line (the disposition status) never widens a pane past its window. The heights below 720 px are the compact rules at the end. */
.cw-crm { --cw-dispo-row: 26px; --cw-slot-label: 96px; --cw-slot-row: 18px; background: var(--surface); border-right: 1px solid var(--rule); padding: var(--space-4); display: grid; grid-template-rows: 22px 18px 60px minmax(0, 1fr) calc(1px + var(--space-2) + 16px + var(--space-1) + 4 * var(--cw-dispo-row) + 3 * var(--space-1)) 64px; row-gap: var(--space-2); grid-template-columns: minmax(0, 1fr); min-height: 0; min-width: 0; overflow: clip; }
.cw-crm-title { font-size: var(--text-section); line-height: 22px; font-weight: var(--wght-strong); }
.cw-crm-title .cw-ph-text { color: var(--ink-3); font-weight: var(--wght-medium); }
.cw-crm-prov { font-size: var(--text-meta); line-height: var(--leading-meta); color: var(--ink-3); }
/* Each slot: a fixed label column and its value. Who keeps the pane's three 20 px rows; every other slot reserves one or
   two 18 px rows, whatever it holds. */
.cw-slot-label { font-size: var(--text-min); line-height: var(--cw-slot-row); font-weight: var(--wght-strong); color: var(--ink-3); letter-spacing: var(--track-small); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.cw-crm-who { display: grid; grid-template-columns: var(--cw-slot-label) minmax(0, 1fr); column-gap: var(--space-2); min-width: 0; }
.cw-crm-who > .cw-slot-label { line-height: 20px; }
.cw-who-rows { display: grid; grid-template-rows: repeat(3, 20px); min-width: 0; }
.cw-who-row { line-height: 20px; }
.cw-who-1 { font-weight: var(--wght-strong); }
.cw-who-3 { font-size: var(--text-meta); color: var(--ink-2); }
/* Slots 2 to 8 reserve 13 rows of --cw-slot-row (CRM_SLOT_ROWS in crmSlots.ts) and six 2 px gaps. */
.cw-crm-body { overflow: clip; border-top: 1px solid var(--rule); padding-top: var(--space-2); display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: calc(13 * var(--cw-slot-row) + 6 * var(--space-0)) minmax(0, 1fr) var(--cw-slot-row); row-gap: var(--space-2); min-height: 0; min-width: 0; }
.cw-crm-slots { display: grid; grid-template-columns: minmax(0, 1fr); grid-auto-rows: max-content; row-gap: var(--space-0); margin: 0; align-content: start; min-height: 0; min-width: 0; overflow: clip; }
.cw-crm-slot { display: grid; grid-template-columns: var(--cw-slot-label) minmax(0, 1fr); column-gap: var(--space-2); min-width: 0; overflow: clip; }
.cw-slot-r1 { height: var(--cw-slot-row); }
.cw-slot-r2 { height: calc(2 * var(--cw-slot-row)); }
.cw-slot-r3 { height: calc(3 * var(--cw-slot-row)); }
.cw-crm-slot dd { margin: 0; min-width: 0; overflow: clip; font-size: var(--text-meta); line-height: var(--cw-slot-row); }
.cw-slot-clamp { display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere; }
.cw-slot-r1 .cw-slot-clamp { -webkit-line-clamp: 1; }
.cw-slot-r2 .cw-slot-clamp { -webkit-line-clamp: 2; }
.cw-slot-r3 .cw-slot-clamp { -webkit-line-clamp: 3; }
.cw-slot-line { display: block; height: var(--cw-slot-row); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* A flowing slot (Last time): each entry starts a new line and wraps inside the slot's clamped rows, so a long first
   entry shows whole before the next one starts (review round 3). */
.cw-slot-flow .cw-slot-line { display: inline; height: auto; white-space: normal; overflow: visible; }
.cw-crm-slot[data-crm-slot='do_not_say'] .cw-slot-clamp, .cw-crm-slot[data-crm-slot='do_not_say'] .cw-slot-line { color: var(--warn); }
.cw-crm-deal { display: flex; gap: var(--space-2); align-items: baseline; min-width: 0; }
.cw-crm-deal > a { flex: none; }
/* The activity log: the card's history, newest first, in fixed 20 px rows that scroll inside the box. */
.cw-crm-section { display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: 16px minmax(0, 1fr); row-gap: var(--space-1); min-height: 0; min-width: 0; overflow: clip; }
.cw-crm-section h3 { font-size: var(--text-min); line-height: 16px; font-weight: var(--wght-strong); color: var(--ink-3); letter-spacing: var(--track-small); }
.cw-crm-history { display: grid; grid-auto-rows: 20px; row-gap: var(--space-0); align-content: start; margin: 0; padding: 0; list-style: none; min-height: 0; overflow-y: auto; scrollbar-gutter: stable; overscroll-behavior: contain; }
.cw-crm-history li { display: grid; grid-template-columns: 6.5em minmax(0, 1fr) 6em; column-gap: var(--space-2); min-width: 0; font-size: var(--text-meta); line-height: 20px; }
.cw-crm-history li > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-hist-type, .cw-hist-when { color: var(--ink-3); }
.cw-hist-when { font-variant-numeric: tabular-nums; }
.cw-crm-links { display: grid; grid-template-columns: var(--cw-slot-label) minmax(0, 1fr); column-gap: var(--space-2); min-width: 0; overflow: clip; font-size: var(--text-meta); line-height: var(--cw-slot-row); }
.cw-crm-links-list { display: flex; gap: var(--space-3); min-width: 0; overflow: clip; white-space: nowrap; }
.cw-crm-links-list > a { flex: none; }
/* Dispositions: four rows of two reserved (all seven defaults in view); more scroll inside the box. The pressed one
   carries aria-busy while it saves and aria-pressed after; no "Saving…" line comes and goes. */
.cw-crm-dispo { display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: 16px minmax(0, 1fr); row-gap: var(--space-1); min-height: 0; min-width: 0; overflow: clip; border-top: 1px solid var(--rule); padding-top: var(--space-2); }
.cw-crm-dispo-head { display: flex; gap: var(--space-3); font-size: var(--text-min); line-height: 16px; color: var(--ink-3); }
.cw-crm-dispo-head > span:first-child { flex: none; font-weight: var(--wght-strong); letter-spacing: var(--track-small); }
.cw-crm-dispo-status { flex: 1 1 auto; text-align: start; }
.cw-crm-dispo-status.is-warn { color: var(--warn); }
.cw-crm-dispo-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: var(--cw-dispo-row); gap: var(--space-1); align-content: start; overflow-y: auto; overscroll-behavior: contain; min-height: 0; margin: 0; padding: 0; }
.cw-crm-dispo-grid .btn { min-height: 0; height: var(--cw-dispo-row); min-width: 0; padding: 0 var(--space-2); font-size: var(--text-meta); justify-content: flex-start; overflow: hidden; }
.cw-crm-dispo-grid .btn[aria-pressed='true'] { border-color: var(--brass); background: var(--brass-tint); }
.cw-crm-dispo-grid .btn[aria-busy='true'] { border-color: var(--brass); }
.cw-crm-dispo-ph { grid-column: 1 / -1; align-self: center; }
.cw-crm-open { display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: 36px 18px; row-gap: var(--space-2); align-self: end; }
.cw-crm-open-status { font-size: var(--text-meta); line-height: var(--leading-meta); color: var(--ink-3); }
/* A short window: tighter padding and gaps, 18 px Who rows, the seven dispositions in three columns of 24 px rows and a
   32 px Open button, so all nine slots and at least one activity row stay in their reserved places. Below 640 px the
   rows are 16 px. The window never opens shorter than its --cw-min-h. */
@media (max-height: 719px) {
  .cw-crm { --cw-dispo-row: 24px; --cw-who-row: 18px; --cw-prov-row: 18px; padding: var(--space-2) var(--space-4); row-gap: var(--space-1);
    grid-template-rows: 22px var(--cw-prov-row) calc(3 * var(--cw-who-row)) minmax(0, 1fr) calc(1px + var(--space-1) + 16px + var(--space-1) + 3 * var(--cw-dispo-row) + 2 * var(--space-1)) calc(32px + var(--space-1) + 18px); }
  .cw-crm-prov { line-height: var(--cw-prov-row); }
  .cw-crm-who > .cw-slot-label, .cw-who-row { line-height: var(--cw-who-row); }
  .cw-who-rows { grid-template-rows: repeat(3, var(--cw-who-row)); }
  .cw-crm-body { padding-top: var(--space-1); row-gap: var(--space-1); }
  .cw-crm-dispo { padding-top: var(--space-1); }
  .cw-crm-dispo-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .cw-crm-open { grid-template-rows: 32px 18px; row-gap: var(--space-1); }
  .cw-crm-open .btn { min-height: 0; height: 32px; }
}
@media (max-height: 639px) {
  .cw-crm { --cw-slot-row: 16px; --cw-who-row: 16px; --cw-prov-row: 16px; --cw-dispo-row: 22px; row-gap: var(--space-0); }
}
`;

export function Dash() {
  return <><span className="cw-dash" aria-hidden="true" /><span className="visually-hidden">None</span></>;
}

type Bar = 'cw-ph-60' | 'cw-ph-45' | 'cw-ph-35';
const Placeholder = ({ bar }: { bar: Bar }) => <span className={`cw-ph ${bar}`} aria-hidden="true" />;

function WhoRow({ value, waiting, n, bar }: { value: string | null; waiting: boolean; n: 1 | 2 | 3; bar: Bar }) {
  return (
    <p className={`cw-who-row cw-who-${n} cw-ellipsis`} title={value ?? undefined}>
      {value ?? (waiting ? <Placeholder bar={bar} /> : <Dash />)}
    </p>
  );
}

/** The window's fixed slot names for the slots it already measured before 4b (see the header). */
const WINDOW_SLOT: Partial<Record<CrmSlotKey, string>> = { open_loop: 'crm-next', deal: 'crm-details' };

/** One of slots 2 to 8: a label and one or two reserved rows. */
function SlotRow({ slot, waiting }: { slot: CrmSlot; waiting: boolean }) {
  const empty = slotEmpty(slot);
  const text = slotText(slot);
  let value;
  if (empty) value = <span className="cw-slot-line">{waiting ? <Placeholder bar={slot.rows === 2 ? 'cw-ph-60' : 'cw-ph-35'} /> : <Dash />}</span>;
  else if (slot.key === 'deal') {
    value = (
      <span className="cw-crm-deal">
        <span className="cw-ellipsis" title={text || undefined}>{text || <Dash />}</span>
        {slot.links.map(l => <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" title={l.host}>{l.label}</a>)}
      </span>
    );
  } else if (slot.mode === 'rows') value = slot.lines.slice(0, slot.rows).map((l, i) => <span key={i} className="cw-slot-line" title={l ?? undefined}>{l}</span>);
  else if (slot.mode === 'flow') {
    const lines = slot.lines.slice(0, slot.rows).filter((l): l is string => !!l);
    value = (
      <span className="cw-slot-clamp cw-slot-flow" title={lines.join('\n')}>
        {lines.map((l, i) => <Fragment key={i}>{i ? <>{' '}<br /></> : null}<span className="cw-slot-line">{l}</span></Fragment>)}
      </span>
    );
  } else value = <span className="cw-slot-clamp" title={text}>{text}</span>;
  return (
    <div className={`cw-crm-slot cw-slot-r${slot.rows}`} data-crm-slot={slot.key} data-slot={WINDOW_SLOT[slot.key]}>
      <dt className="cw-slot-label" title={slot.label}>{slot.label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Dispositions({ crm, dispo, onPick }: { crm: CrmPaneModel; dispo: CrmDispoModel; onPick: (id: string) => void }) {
  // Pressed means logged for THIS call, the same source the wrap-up reads (review round 2). The disposition the card
  // carries (bcio-net's candidate.last_disposition) is an earlier call's; it shows in the activity log, not as pressed.
  const chosen = dispo.selected;
  return (
    <section className="cw-crm-dispo" data-slot="crm-dispo" aria-label="Disposition">
      <p className="cw-crm-dispo-head">
        <span>Disposition</span>
        <span className={`cw-crm-dispo-status cw-ellipsis${dispo.warn ? ' is-warn' : ''}`} data-slot="crm-dispo-status" role="status" title={dispo.status || undefined}>{dispo.status}</span>
      </p>
      <div className="cw-crm-dispo-grid" role="group" aria-label="Call disposition">
        {crm.dispositions.length ? crm.dispositions.map(d => (
          <button
            key={d.id}
            type="button"
            className="btn"
            aria-pressed={chosen === d.id}
            aria-busy={dispo.busy === d.id || undefined}
            title={d.label}
            // Not disabled while one saves: seven buttons dimming for a round trip would flash. A second press waits.
            onClick={() => { if (!dispo.busy) onPick(d.id); }}
          >
            <span className="cw-ellipsis">{d.label}</span>
          </button>
        )) : <span className="cw-crm-dispo-ph">{crm.waiting ? <Placeholder bar="cw-ph-45" /> : <Dash />}</span>}
      </div>
    </section>
  );
}

export interface CrmPaneProps {
  crm: CrmPaneModel;
  status: string;
  dispo: CrmDispoModel;
  onOpen: () => void;
  onDisposition: (id: string) => void;
  /** The raw card the pane shows (the call's own, or the last call's with this number; null for none), for the 4b
   * slots. Left out, the slots come from `crm` alone (who, the next step as the open loop, stage and owner, history). */
  card?: CallContextCard | null;
  /** The window's clock (dates in the activity log drop the year inside the current one). */
  now?: number;
}

export function CrmPane({ crm, status, dispo, onOpen, onDisposition, card, now }: CrmPaneProps) {
  const model = crmSlotsModel(card, crm, now);
  const [who, ...rest] = model.slots;
  const links = rest[rest.length - 1]!;
  const middle = rest.slice(0, -1);
  return (
    <section className="cw-crm" data-slot="crm" aria-label="CRM" data-crm-state={crm.state}>
      <h2 className="cw-crm-title cw-ellipsis" data-slot="crm-title" title={crm.title ?? undefined}>
        {crm.title ?? <span className="cw-ph-text">CRM card</span>}
      </h2>
      <p className="cw-crm-prov cw-ellipsis" data-slot="crm-prov">{crm.provenance}</p>
      <div className="cw-crm-who" data-slot="crm-who" data-crm-slot="who" role="group" aria-label={who!.label}>
        <span className="cw-slot-label" aria-hidden="true">{who!.label}</span>
        <div className="cw-who-rows">
          <WhoRow value={who!.lines[0] ?? null} waiting={crm.waiting} n={1} bar="cw-ph-60" />
          <WhoRow value={who!.lines[1] ?? null} waiting={crm.waiting} n={2} bar="cw-ph-45" />
          <WhoRow value={who!.lines[2] ?? null} waiting={crm.waiting} n={3} bar="cw-ph-35" />
        </div>
      </div>
      <div className="cw-crm-body" data-slot="crm-body">
        <dl className="cw-crm-slots" aria-label="Call sheet">
          {middle.map(slot => <SlotRow key={slot.key} slot={slot} waiting={crm.waiting} />)}
        </dl>
        <section className="cw-crm-section" data-slot="crm-history" data-crm-slot="activity" aria-label="Activity log">
          <h3>Activity log</h3>
          <ol className="cw-crm-history" tabIndex={model.activity.length > 2 ? 0 : undefined} aria-label="Activity, newest first">
            {model.activity.length ? model.activity.map(a => (
              <li key={a.key}>
                <span className="cw-hist-type">{a.type}</span>
                <span className="cw-hist-summary" title={a.summary}>{a.summary}</span>
                <span className="cw-hist-when">{a.when}</span>
              </li>
            )) : <li>{crm.waiting ? <Placeholder bar="cw-ph-35" /> : <span><Dash /></span>}</li>}
          </ol>
        </section>
        <p className="cw-crm-links" data-crm-slot="links">
          <span className="cw-slot-label">{links.label}</span>
          <span className="cw-crm-links-list">
            {links.links.length
              ? links.links.map(l => <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" title={l.host}>{l.label}</a>)
              : crm.waiting ? <Placeholder bar="cw-ph-35" /> : <span><Dash /></span>}
          </span>
        </p>
      </div>
      <Dispositions crm={crm} dispo={dispo} onPick={onDisposition} />
      <div className="cw-crm-open" data-slot="crm-open">
        <button type="button" className="btn btn-block" disabled={!crm.primary} onClick={onOpen}>
          <Icon icon={ExternalLink} /> Open in CRM
        </button>
        <p className="cw-crm-open-status cw-ellipsis" data-slot="crm-open-status">{status}</p>
      </div>
    </section>
  );
}
